import React, { createContext, useContext, useState, useEffect } from "react";
import ReactDOM from "react-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Slide,
} from "@mui/material";

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  // 🔔 Toast Notification State
  const [openNotification, setOpenNotification] = useState({
    open: false,
    message: "",
    variant: "info",
    duration: 4000,
  });

  // Auto-close logic
  useEffect(() => {
    if (openNotification.open) {
      const timer = setTimeout(() => {
        setOpenNotification({ open: false, message: "", variant: "info", duration: 4000 });
      }, openNotification.duration);
      return () => clearTimeout(timer);
    }
  }, [openNotification]);

  // ⚠️ Confirm Dialog State
  const [confirmState, setConfirmState] = useState({
    open: false,
    title: "",
    message: "",
    resolve: null,
    onConfirm: null,
    onCancel: null,
  });

  // Global notify methods
  const notify = {
    success: (message, duration = 4000) =>
      setOpenNotification({ open: true, message, variant: "success", duration }),
    error: (message, duration = 4000) =>
      setOpenNotification({ open: true, message, variant: "error", duration }),
    info: (message, duration = 4000) =>
      setOpenNotification({ open: true, message, variant: "info", duration }),
    warning: (message, duration = 4000) =>
      setOpenNotification({ open: true, message, variant: "warning", duration }),

    confirm: ({ title = "Are you sure?", message = "", onConfirm, onCancel }) =>
      new Promise((resolve) => {
        setConfirmState({ open: true, title, message, resolve, onConfirm, onCancel });
      }),
  };

  // Confirm dialog handlers
  const handleConfirmClose = (result) => {
    if (confirmState.resolve) confirmState.resolve(result);
    if (result && confirmState.onConfirm) confirmState.onConfirm();
    if (!result && confirmState.onCancel) confirmState.onCancel();
    setConfirmState({ ...confirmState, open: false, resolve: null });
  };

  return (
    <NotificationContext.Provider value={notify}>
      {children}

      {/* 🔔 Toast Notification */}
      {ReactDOM.createPortal(
        openNotification.open && (
          <div
            style={{
              position: "fixed",
              top: "10px",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 9999,
              display: "flex",
              justifyContent: "center",
              width: "100%",
              pointerEvents: "none",
            }}
          >
            <Slide direction="down" in={true} mountOnEnter unmountOnExit>
              <Alert
                severity={openNotification.variant}
                onClose={() =>
                  setOpenNotification({ open: false, message: "", variant: "info", duration: 4000 })
                }
                style={{
                  transform: "translate(-50%, -50%)",
                  minWidth: "68.5%",
                  maxHeight: "30px",
                  display: "flex",
                  alignItems: "center",
                  wordBreak: "break-word",
                  pointerEvents: "all",
                }}
              >
                {openNotification.message}
              </Alert>
            </Slide>
          </div>
        ),
        document.body
      )}

      {/* ⚠️ Confirm Dialog */}
      <Dialog open={confirmState.open} onClose={() => handleConfirmClose(false)}>
        <DialogTitle>{confirmState.title}</DialogTitle>
        <DialogContent>{confirmState.message}</DialogContent>
        <DialogActions>
          <Button onClick={() => handleConfirmClose(false)}>Cancel</Button>
          <Button
            onClick={() => handleConfirmClose(true)}
            variant="contained"
            color="primary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </NotificationContext.Provider>
  );
};

export const useNotification = () => useContext(NotificationContext);
